import { Separator } from "@/components/ui/separator";
import { User } from "@/models/user.model";
import React, { useEffect, useState } from "react";
import userImage from "@/assets/images/user.png";
import NumberInput from "@/components/form/NumberInput";
import { toast } from "@/hooks/use-toast";

import { useNavigate } from "react-router-dom";
import BackButton from "@/components/buttons/BackButton";
import { getUser } from "@/api/users";
import EditButton from "@/components/buttons/EditButton";
import { PaymentInfoDTO } from "@/models/payment-info.model";
import { Skeleton } from "@/components/ui/skeleton";
import { getLeaveBalancesOfUser } from "@/api/leave-balances";
import { LeaveBalanceDTO } from "@/models/leave-balances.model";
import LeaveBalanceDataTable from "@/components/leave-balance/table/LeaveBalanceDataTable";
import { Payslip } from "@/models/payslips.model";
import { formatDateLocale, formatDateToString, months, toDateFormatted, toDateMonthYear } from "@/utils/date-format";
import { updateOrCreatePayslipSnapshotDetails } from "@/api/payslips";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { EmploymentStatus } from "@/models/employment-info.model";
import EmploymentStatusTag from "@/components/tags/EmploymentStatusTag";

interface Props {
  userId: number;
  year: number;
  month: number;
  payslip?: Payslip;
}

function SalaryInfoForm({ payslip, userId, year, month }: Props) {
  const navigate = useNavigate();

  const [err, setErr] = useState<string>("");
  const [isReadOnly, setIsReadOnly] = useState<boolean>(true);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const [basicSalary, setBasicSalary] = useState<number>(0);
  const [currentBasicSalary, setCurrentBasicSalary] = useState<number>(0);
  const [employeeCpfContributionRate, setEmployeeCpfContributionRate] =
    useState<number>(0);
  const [employerCpfContributionRate, setEmployerCpfContributionRate] =
    useState<number>(0);

  const [paymentInfo, setPaymentInfo] = React.useState<PaymentInfoDTO>();
  const [user, setUser] = useState<User>();

  const [leaveBalance, setLeaveBalance] = useState<LeaveBalanceDTO[]>([]);

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        // Always fetch user and leave balances
        const [fetchedUser, leaveBalanceRes] = await Promise.all([
          getUser(userId),
          getLeaveBalancesOfUser(userId, year),
        ]);
        setUser(fetchedUser);
        if (!fetchedUser.paymentInfo) {
          setErr("User does not have payment information.");
          return;
        }
        setPaymentInfo(fetchedUser.paymentInfo);
        setLeaveBalance(leaveBalanceRes);

        setCurrentBasicSalary(fetchedUser.paymentInfo?.basicSalary || 0);
        if (!payslip) {
          // Populate from payment info
          setBasicSalary(fetchedUser.paymentInfo.basicSalary || 0);
          setEmployeeCpfContributionRate(fetchedUser.paymentInfo.employeeCpfContributionRate || 0);
          setEmployerCpfContributionRate(fetchedUser.paymentInfo.employerCpfContributionRate || 0);

        } else {
          // Populate from payslip snapshot
          setBasicSalary(payslip?.basicSalarySnapshot || 0);
          setEmployeeCpfContributionRate(payslip?.employeeCpfRateSnapshot || 0);
          setEmployerCpfContributionRate(payslip?.employerCpfRateSnapshot || 0);
        }
      } catch (err) {
        console.error("Error initializing form data", err);
      }
    };

    fetchInitialData();
  }, [userId, payslip]);



  const handleCancel = async () => {
    setErr("");
    setIsReadOnly(true);

    if (payslip) {
      setBasicSalary(payslip?.basicSalarySnapshot || 0);
      setEmployeeCpfContributionRate(payslip?.employeeCpfRateSnapshot || 0);
      setEmployerCpfContributionRate(payslip?.employerCpfRateSnapshot || 0);
    } else if (paymentInfo) {
      setBasicSalary(paymentInfo?.basicSalary || 0);
      setEmployeeCpfContributionRate(paymentInfo?.employeeCpfContributionRate || 0);
      setEmployerCpfContributionRate(paymentInfo?.employerCpfContributionRate || 0);
    }
  };

  const isNoChange = () => {
    let res;
    if (payslip) {
      res =
        payslip?.basicSalarySnapshot === basicSalary &&
        payslip?.employeeCpfRateSnapshot ===
        employeeCpfContributionRate &&
        payslip?.employerCpfRateSnapshot === employerCpfContributionRate;
    } else {
      res =
        paymentInfo?.basicSalary === basicSalary &&
        paymentInfo?.employeeCpfContributionRate ===
        employeeCpfContributionRate &&
        paymentInfo?.employerCpfContributionRate ===
        employerCpfContributionRate
    }

    return res;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    setIsSubmitting(true);
    e.preventDefault();
    if (!basicSalary || basicSalary === 0) {
      setErr("Salary must be more than 0.");
      return;
    }
    if (
      isNaN(employeeCpfContributionRate) ||
      isNaN(employerCpfContributionRate)
    ) {
      setErr("Please fill in the relevant CPF fields.");
      return;
    }

    try {
      const res = await updateOrCreatePayslipSnapshotDetails(
        userId,
        year,
        month,
        {
          selfHelpGroupSnapshot: paymentInfo?.selfHelpGroup,
          basicSalarySnapshot: basicSalary,
          employeeCpfRateSnapshot: employeeCpfContributionRate,
          employerCpfRateSnapshot: employerCpfContributionRate,
        }
      );

      if (res) {

        setIsReadOnly(true);
        toast({
          title: "Salary information updated successfully",
        });
      } else {
        toast({
          title: "Failed to update salary information",
          description: "Please try again",
          variant: "destructive",
        });
      }
    } catch (e) {
      console.error(e);
      toast({
        title: "An error occurred",
        description: "Please try again",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if ((!paymentInfo && !payslip) || !user) {
    return <Skeleton className="h-full w-full " />;
  }

  return (
    <div className="w-full h-full flex flex-col p-4 gap-6">
      <div className="flex flex-col gap-2">
        <BackButton
          link="/admin/process-pay"
          state={{ year, month }}
          label="Process Pay"
        />
        <div className="flex flex-row gap-5 w-full items-center">
          <div className="flex w-1/4 items-center justify-center">
            <img src={userImage} alt="User Avatar" />
          </div>
          <div className="w-2/3 flex flex-col gap-1">
            <div className="font-semibold text-xl">
              {user.fullName}
            </div>
            <EmploymentStatusTag status={user.employmentInfo?.employmentStatus || EmploymentStatus.ACTIVE} />
            {user.employmentInfo?.hireDate && <div className="text-xs text-gray-500">Hire Date: {toDateMonthYear(user.employmentInfo?.hireDate)}</div>}
            {user.employmentInfo?.lastDayOfWork && <div className="text-xs text-gray-500">Last Day of Work: {toDateMonthYear(user.employmentInfo?.lastDayOfWork)}</div>}

          </div>
        </div>
      </div>

      <Separator />
      <form className="w-full h-full flex flex-col gap-6">
        <div className="w-full flex flex-col gap-2">
          <div className="flex flex-row w-full justify-between items-center"><div className="font-semibold">Payment Information Used</div>
            <EditButton
              handleEditing={() => setIsReadOnly(false)}
              isEditing={isReadOnly}
              handleCancel={handleCancel}
              isNoChange={isNoChange}
              handleSave={handleSubmit}
              isSubmitting={isSubmitting}
            /></div>

          <div className="text-sm text-gray-500">
            The payslip for the period of{" "}
            <span className="text-black font-medium">
              {months[month - 1]} {year}
            </span>{" "}
            was generated using the salary information below, which may differ from the employee's{" "}
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="text-black underline cursor-help">
                  most recently updated information
                </span>
              </TooltipTrigger>
              <TooltipContent>
                <p className="font-bold">Latest Details (as reflected on profile)</p>
                <p>Basic Salary: ${currentBasicSalary}</p>
                <p>Employee CPF Contribution Rate: {paymentInfo?.employeeCpfContributionRate}%</p>
                <p>Employer CPF Contribution Rate: {paymentInfo?.employerCpfContributionRate}%</p>
              </TooltipContent>
            </Tooltip>.
            <br />Use MOM's salary calculator for incomplete month {" "}
            <a href="https://www.mom.gov.sg/employment-practices/salary/calculate-pay-for-incomplete-month" target="_blank" className="text-blue-500 underline" rel="noreferrer">here</a>.
          </div>
        </div>


        <div className="flex gap-2">
          <NumberInput
            label="Basic Salary"
            value={basicSalary}
            setValue={setBasicSalary}
            readOnly={isReadOnly}
          />

        </div>

        <div className="flex gap-2">
          <NumberInput
            label="Employee CPF Contribution Rate (%)"
            value={employeeCpfContributionRate}
            setValue={setEmployeeCpfContributionRate}
            readOnly={isReadOnly}
          />
          <NumberInput
            label="Employer CPF Contribution Rate (%)"
            value={employerCpfContributionRate}
            setValue={setEmployerCpfContributionRate}
            readOnly={isReadOnly}
          />
        </div>

        {err && <p className="text-red-500 text-sm">{err}</p>}
      </form>
      <Separator />
      <div className="w-full h-full flex flex-col overflow-y-auto">
        <div className="font-semibold">Leave Balance</div>
        <LeaveBalanceDataTable leaveBalances={leaveBalance} />
      </div>
    </div>
  );
}

export default SalaryInfoForm;
